import { LatLngExpression } from "leaflet";
import queryString from 'query-string';
import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import { io } from "socket.io-client";
import { getUserAddresses } from "../api/addresses";
import { getAllUserCustomData, getUserDetails, getUserProjects, getUserProjectsAndItems, isLoggedIn } from "../api/auth";
import { getUserCartItems } from "../api/cart";
import { getAllUserMessages } from "../api/messages";
import { getCurrentUserCompanyPurchaseRequests, getUserCompanyOrdersRequests } from "../api/orders";
import { getUserTokens as _getUserTokens, getTokensPurchaseConfig, getUserCompanyTokens } from "../api/tokens";
import { AccountBase, AddressBookItem, BulkItem, CartBulkItem, CartItem, CompanyTokens, FullAccount, OrderRequestItem, PaymentMethod, PaymentType, Project, ProjectItem, TransactionDetails, UserCustomDataPoint, UserMessage, UserTokens } from "../core/global";
import { getPaymentOptions } from "../helpers/cart";

const API_BASE = import.meta.env.VITE_API_BASE;



let socket: any = null;

if (typeof window !== "undefined") {
  socket = io(API_BASE);
}

const accountInitialValue: AccountBase = {
  loggedIn: false,
  checked: false,
  uid: null,
  email: null,
  first_name: null,
  last_name: null,
  organisation: [],
};

interface AccountContextProps {
  customDataPoints: UserCustomDataPoint[],
  refetchCustomDataPoints: (onComplete?: any ) => void,
  showLoginModal: boolean
  setShowLoginModal: React.Dispatch<React.SetStateAction<boolean>>;
  refetchCompanyRequests: () => void
  companyPurchaseRequests: OrderRequestItem[] | null
  currentUserPurchaseRequests: OrderRequestItem[] | null
  transaction: TransactionDetails | null
  setTransaction: React.Dispatch<React.SetStateAction<TransactionDetails | null>>
  bulkTokenPurchase: BulkItem[];
  account: FullAccount;
  setAccount: React.Dispatch<React.SetStateAction<FullAccount>>;
  refetchUserDetails: () => void
  refetchCart: () => void
  cart: CartItem[] | null
  cartError: string[] | null
  setCartError: React.Dispatch<React.SetStateAction<string[] | null>>; 
  showCart: boolean
  setShowCart: React.Dispatch<React.SetStateAction<boolean>>;
  showPurchaseData: boolean
  setShowPurchaseData: React.Dispatch<React.SetStateAction<boolean>>;
  addressBook: AddressBookItem[]
  refetchAddressBook: (onComplete?: any) => void
  isVatExempt: boolean
  projects: ProjectItem[]
  refetchProjects: (onComplete?: any ) => void
  projectPoints: LatLngExpression[]
  setProjectPoints: React.Dispatch<React.SetStateAction<LatLngExpression[]>>;
  selectedPoint: LatLngExpression | null
  setSelectedPoint: React.Dispatch<React.SetStateAction<LatLngExpression | null>>;
  userProjects: Project[]
  refetchUserProjects: (onComplete?: any) => void
  purchaseTokens: CartBulkItem[]
  updatePurchaseTokens: React.Dispatch<React.SetStateAction<CartBulkItem[]>>
  userTokens: UserTokens[] | null
  userCompanyTokens: CompanyTokens[] | null
  getUserTokens: () => void
  userPaymentMethods: PaymentMethod[] | null
  userActivePaymentMethod: PaymentMethod | null
  setUserActivePaymentMethod: React.Dispatch<React.SetStateAction<PaymentMethod | null>>;
  userTokensTotal: number
  paymentType: PaymentType | null
  activeOrderId: string | number | null
  setActiveOrderId: React.Dispatch<React.SetStateAction<string | number | null>>;
  showApprovePurchaseModal: number | string | null
  setShowApprovePurchaseModal: React.Dispatch<React.SetStateAction<number | string | null>>;
  userMessages: UserMessage[]
  refetchUserMessages: () => void,
  setUserMessages: React.Dispatch<React.SetStateAction<UserMessage[]>>,
  showProjectPointsOnMap: boolean
  setShowProjectPointsOnMap: React.Dispatch<React.SetStateAction<boolean>>;
}

const initialValue: AccountContextProps = {
  customDataPoints: [],
  refetchCustomDataPoints: () => {},
  showProjectPointsOnMap: false,
  setShowProjectPointsOnMap: () => {},
  showLoginModal: false,
  setShowLoginModal: () => {},
  userMessages: [],
  refetchUserMessages: () => {},
  setUserMessages: () => {},
  refetchCompanyRequests: () => {},
  companyPurchaseRequests: null,
  currentUserPurchaseRequests: null,
  bulkTokenPurchase: [],
  transaction: null,
  setTransaction: () => {},
  account: accountInitialValue,
  setAccount: () => {},
  refetchUserDetails: () => {},
  refetchCart: () => {},
  cart: null,
  cartError: null,
  setCartError: () => {},
  showCart: false,
  setShowCart: () => {},
  showPurchaseData: false,
  setShowPurchaseData: () => {},
  addressBook: [],
  refetchAddressBook: () => {},
  isVatExempt: false,
  projects: [],
  refetchProjects: () => {},
  projectPoints: [],
  setProjectPoints: () => {},
  selectedPoint: null,
  setSelectedPoint: () => {},
  userProjects: [],
  refetchUserProjects: () => {},
  purchaseTokens: [],
  updatePurchaseTokens: () => {},
  userTokens: null,
  userCompanyTokens: null,
  getUserTokens: () => {},
  userPaymentMethods: null,
  userActivePaymentMethod: null,
  setUserActivePaymentMethod: () => {},
  userTokensTotal: 0,
  paymentType: null,
  activeOrderId: null,
  setActiveOrderId: () => {},
  showApprovePurchaseModal: null,
  setShowApprovePurchaseModal: () => {},
};

export const AccountContext = createContext<AccountContextProps>(initialValue);

interface AccountProviderProps {
  children?: React.ReactNode;
}

export const AccountProvider = ({ children }: AccountProviderProps) => {
  const [customDataPoints, setCustomDataPoints] = useState<UserCustomDataPoint[]>(initialValue.customDataPoints);

  const [showLoginModal, setShowLoginModal] = useState<boolean>(initialValue.showLoginModal);
  const [showProjectPointsOnMap, setShowProjectPointsOnMap] = useState<boolean>(initialValue.showProjectPointsOnMap);
  const [companyPurchaseRequests, setCompanyPurchaseRequests]= useState(initialValue.companyPurchaseRequests)
  const [currentUserPurchaseRequests, setCurrentUserPurchaseRequests]= useState(initialValue.currentUserPurchaseRequests)
  const [transaction, setTransaction] = useState(initialValue.transaction)
  const [activeOrderId, setActiveOrderId] = useState(initialValue.activeOrderId)
  const [userProjects, setUserProjects] = useState(initialValue.userProjects)
  const [selectedPoint, setSelectedPoint] = useState(initialValue.selectedPoint)
  const [projectPoints, setProjectPoints] = useState(initialValue.projectPoints)
  const [projects, setProjects] = useState(initialValue.projects)
  const [addressBook, setAddressBook] = useState(initialValue.addressBook);
  const [account, setAccount] = useState(accountInitialValue);
  const [cart, setCart] = useState(initialValue.cart)
  const [cartError, setCartError] = useState(initialValue.cartError)
  const [showPurchaseData, setShowPurchaseData] = useState(initialValue.showPurchaseData)
  const [showCart, setShowCart] = useState(initialValue.showCart)
  const [showApprovePurchaseModal, setShowApprovePurchaseModal] = useState(initialValue.showApprovePurchaseModal)
  const [purchaseTokens, updatePurchaseTokens] = useState(initialValue.purchaseTokens)
  const [bulkTokenPurchase, setBulkTokenPurchase] = useState<BulkItem[]>(initialValue.bulkTokenPurchase)
  const [userTokens, setUserTokens] = useState(initialValue.userTokens)
  const [userCompanyTokens, setUserCompanyTokens] = useState(initialValue.userCompanyTokens)
  const [userPaymentMethods, setUserPaymentMethods] = useState(initialValue.userPaymentMethods)
  const [userActivePaymentMethod, setUserActivePaymentMethod] = useState(initialValue.userActivePaymentMethod)
  const [userMessages, setUserMessages] = useState<UserMessage[]>(initialValue.userMessages)

  useEffect(() => {
    const q = queryString.parse(location.search);
    const { success, processing_token, transaction_id, tokens } = q;
    if (!success || !processing_token || !transaction_id) {
      return
    }
    const trans: TransactionDetails = {
      isPaymentSuccess: !!(success && success === 'true'),
      processingToken: `${processing_token}`,
      transactionId: `${transaction_id}`,
      tokens: Number(tokens) || null,
      isRedirect: true,
      type: "card",
    }
    setTransaction(trans)
  }, [location.search])

  useEffect(() => {
    const execute = async () => {
      const {response} = await  getTokensPurchaseConfig();

      if (response) {
        const {config} = response
        if (config && Array.isArray(config)) {
          setBulkTokenPurchase(config as BulkItem[])
        }
      }
    }
    execute()
  }, [])
  
  const refetchUserMessages = useCallback(async () => {
    const {response} = await getAllUserMessages();
    if (response) {
      const {messages} = response
      if (messages && Array.isArray(messages)) {
        setUserMessages( m => {
          return [...m, ...messages as UserMessage[]]
        })
      }
    }
  }, [])

  const refetchCompanyRequests = useCallback(() => {
    const execute = async () => {
      const {response} = await getUserCompanyOrdersRequests();

      if (response) {
        const {requests} = response
        if (requests && Array.isArray(requests)) {
          setCompanyPurchaseRequests(requests as OrderRequestItem[])
        }
      }
    }
    const executeUserRequests = async () => {
      const {response} = await getCurrentUserCompanyPurchaseRequests();

      if (response) {
        const {requests} = response
        if (requests && Array.isArray(requests)) {
          setCurrentUserPurchaseRequests(requests as OrderRequestItem[])
        }
      }
    }
    execute()
    executeUserRequests()
  }, [])

  const getUserTokens = useCallback(() =>{
    const execute = async () => {
      const {response: userResponse} = await _getUserTokens()
      if (userResponse) {
        const {tokens: {user_tokens}} = userResponse
        if (user_tokens && Array.isArray(user_tokens) && user_tokens.length > 0) {
          setUserTokens(user_tokens)
        } else {
          setUserTokens([])
        }
      }
      
      // TODO: get user company tokens
      let userCompanyTokensValue = []
      const {response: companyResponse} = await getUserCompanyTokens()
      if (companyResponse) {
        const {tokens: {user_company_tokens}} = companyResponse
        if (user_company_tokens && Array.isArray(user_company_tokens) && user_company_tokens.length > 0) {
          userCompanyTokensValue = user_company_tokens
        }
      }
      setUserCompanyTokens(userCompanyTokensValue)
    }
    execute()
  }, [])

  // On page load - check if token exists and account is logged in
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
  
    const checkActiveSession = async () => {
      const loggedIn = await isLoggedIn();
  
      setAccount((currAccount: FullAccount) => ({
        ...currAccount,
        loggedIn,
        checked: true,
      }));
  
      // Session is gone — stop polling. No point re-checking a dead session
      // every 10 min (this is the source of the recurring 401s in the logs).
      if (!loggedIn && interval) {
        clearInterval(interval);
        interval = undefined;
      }
    };
  
    checkActiveSession();
    interval = setInterval(checkActiveSession, 1000 * 60 * 10);
  
    return () => { if (interval) clearInterval(interval); };
  }, []);
  
  const refetchUserProjects = useCallback( async (onComplete?: () => void) => {
    const {response} = await getUserProjects();
    if (response) {
      const {projects} = response
      if (projects && Array.isArray(projects)) {
        setUserProjects(projects as Project[])
      }
    }
    if (onComplete) {
      onComplete()
    }
  }, [])

  const refetchAddressBook = useCallback(async (onComplete?: () => void) => {
    const {response} = await getUserAddresses();
    if (response) {
      const {addresses} = response
      if (addresses && Array.isArray(addresses)) {
        setAddressBook(addresses)
      }
    }
    if (onComplete) {
      onComplete()
    }
  }, [])

  const refetchProjects = useCallback(async (onComplete?: () => void) => {
    const {response} = await getUserProjectsAndItems({});
    if (response) {
      const {projects} = response
      if (projects && Array.isArray(projects)) {
        setProjects(projects)
      }
    }
    if (onComplete) {
      onComplete()
    }
  }, [])


  const refetchCustomDataPoints = useCallback( async (onComplete?: () => void) => {
    // customDataPoints
    const {response} = await getAllUserCustomData();
    if (response) {
      const {data} = response
      if (data && Array.isArray(data)) {
        setCustomDataPoints(data)
      }
    }
    if (onComplete) {
      onComplete()
    }
  }, [])


  const refetchCart = useCallback(async () => {
    const items = await getUserCartItems();
    const {response} = items
    if (response && Object.prototype.hasOwnProperty.call(response, 'cart')) {
      const {cart} = response
      setCart([...cart])
    } else {
      setCart(initialValue.cart)
    }
  }, [])


  useEffect(() => {
    if (account.loggedIn && account?.uid) {
      refetchCart();
      refetchAddressBook();
      refetchUserProjects();
      getUserTokens();
      refetchUserMessages();
      refetchCustomDataPoints();
    }
  }, [JSON.stringify(account)])

  const refetchUserDetails = useCallback(async () => {
    const {response} = await getUserDetails()
    if (response) {
      setAccount((currAccount: FullAccount) => {
        return {
          ...currAccount,
          ...response.account
        };
      });
    }
  }, [])

  // If logged in, fetch details
  useEffect(() => {
    if (account.loggedIn) {
      if (!account?.uid) {
        refetchUserDetails()
      }
    }
  }, [account, refetchUserDetails])
  
  useEffect(() => {
    if (account.loggedIn && account?.uid && account?.organisation) {
      socket.on(`message-${account.uid}`, () => {
        refetchUserMessages()
        refetchUserProjects(() => {
          refetchProjects()
          refetchCompanyRequests()
        })
      });
      
      if (account?.organisation && Array.isArray(account.organisation)) {
        const orgIds = account.organisation.map(item => item.id)
        orgIds.forEach( id => {
          socket.on(`organisation-update-${id}`, () => {
            refetchCompanyRequests()
          });
        })
      }
    }
  }, [account.loggedIn, account?.uid, account?.organisation])


  const isVatExempt: boolean = useMemo(() => {

    if (userActivePaymentMethod) {
      if (userActivePaymentMethod.type === "company-po" || userActivePaymentMethod.type === "company-stripe") {
        const {config} = userActivePaymentMethod
        if (config && Object.prototype.hasOwnProperty.call(config, "vat_exempt")) {
          const {vat_exempt} = config
          return !!vat_exempt
        }
      }
      if (userActivePaymentMethod.type === "stripe") {
        // Individual payment - are not vat exempt
        return false
      }
    }
    if (account && account?.organisation) {
      const {organisation, uid} = account
      if (organisation && Array.isArray(organisation)) {
        const found = organisation.find( item => item.created_by === uid)
        if (found) {
          return !!(found?.vat_exempt || found?.vat_exempt === "true")
        }
      }
    }
    return false
  }, [account, JSON.stringify(userActivePaymentMethod)])

  const userTokensTotal = useMemo(() => {
    if (Array.isArray(userTokens) && userTokens.length > 0) {
      return userTokens.reduce((acc, item) => {
        const {tokens_value} = item
        if (tokens_value && !isNaN(tokens_value)) {
          return acc + Number(tokens_value)
        }
        return acc
      }, 0)
    }
    return 0
  }, [userTokens])

  const paymentType = useMemo(() => {
    if (userActivePaymentMethod) {
      if (userActivePaymentMethod.type === "po" || userActivePaymentMethod.type === "company-po") {
        return "po"
      }
      if (userActivePaymentMethod.type === "tokens" || userActivePaymentMethod.type === "company-tokens") {
        return "tokens"
      }
      if (userActivePaymentMethod.type === "subscription") {
        return "subscription"
      }
      return "card"
    }
    return null
  }, [JSON.stringify(userActivePaymentMethod)])

  useEffect(() => {
    const methods = getPaymentOptions({userTokens, userCompanyTokens, isTokenOrder: purchaseTokens.length > 0})
    setUserPaymentMethods(methods)
    // set default payment method
    if (methods !== null && Array.isArray(methods) && methods.length > 0) {
      setUserActivePaymentMethod(methods[0])
    }
  }, [userTokens, userCompanyTokens, purchaseTokens])

  return (
    <AccountContext.Provider
      value={{
        showLoginModal,
        setShowLoginModal,      
        currentUserPurchaseRequests,
        userMessages,
        refetchUserMessages,
        setUserMessages,
        companyPurchaseRequests,
        bulkTokenPurchase,
        refetchUserDetails,
        account,
        setAccount,
        cart,
        cartError,
        setCartError,
        refetchCart,
        showCart, 
        setShowCart,
        showPurchaseData, 
        setShowPurchaseData,
        addressBook,
        refetchAddressBook,
        isVatExempt,
        projects,
        refetchProjects,
        projectPoints, 
        setProjectPoints,
        selectedPoint,
        setSelectedPoint,
        userProjects,
        refetchUserProjects,
        purchaseTokens, 
        updatePurchaseTokens,
        userTokens,
        userCompanyTokens,
        getUserTokens,
        userPaymentMethods,
        userActivePaymentMethod, 
        setUserActivePaymentMethod,
        userTokensTotal,
        paymentType,
        activeOrderId, 
        setActiveOrderId,
        transaction,
        setTransaction,
        refetchCompanyRequests,
        showApprovePurchaseModal, 
        setShowApprovePurchaseModal,
        showProjectPointsOnMap, 
        setShowProjectPointsOnMap,
        customDataPoints,
        refetchCustomDataPoints,
      }}
    >
      {children}
    </AccountContext.Provider>
  );
};
